import { useEffect, useMemo, useState, useRef } from 'react';
import { axios } from '../App';
import { toast } from 'sonner';
import {
  Crosshair,
  Flame,
  ShieldCheck,
  ShieldOff,
  Plane,
  Waves,
  Droplets,
  Activity,
  FileLock2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Zap,
} from 'lucide-react';

const DOMAIN_ICON = { air: Plane, surface: Waves, underwater: Droplets };
const DOMAIN_COLOR = { air: 'text-sky-400', surface: 'text-emerald-400', underwater: 'text-blue-400' };

const WEAPON_KIND = {
  rf_jammer: { kind: 'soft', label: 'RF Jammer', accent: 'emerald' },
  gps_spoofer: { kind: 'soft', label: 'GPS Spoofer', accent: 'emerald' },
  comm_disruptor: { kind: 'soft', label: 'Comm Disruptor', accent: 'emerald' },
  cyber_takeover: { kind: 'soft', label: 'Cyber Takeover', accent: 'emerald' },
  acoustic_blaster: { kind: 'soft', label: 'Acoustic Blaster', accent: 'emerald' },
  anti_drone_gun: { kind: 'hard', label: 'Anti-Drone Gun', accent: 'amber' },
  dew: { kind: 'hard', label: 'Directed Energy', accent: 'red' },
  anti_drone_missile: { kind: 'hard', label: 'Missile', accent: 'red' },
  kinetic_interceptor: { kind: 'hard', label: 'Kinetic Interceptor', accent: 'red' },
  net_launcher: { kind: 'hard', label: 'Net Launcher', accent: 'amber' },
};

const ASSET = { lat: 17.6868, lng: 83.2185 };
const ENGAGE_RADIUS_M = 5000;

function haversineM(a, b, c, d) {
  const R = 6371000;
  const p1 = (a * Math.PI) / 180;
  const p2 = (c * Math.PI) / 180;
  const dp = ((c - a) * Math.PI) / 180;
  const dl = ((d - b) * Math.PI) / 180;
  const x = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

function timeAgo(iso) {
  if (!iso) return '—';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}

function RuleLight({ ok, label }) {
  return (
    <div
      className={`flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
        ok
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
          : 'border-red-500/40 bg-red-500/10 text-red-300'
      }`}
      data-testid={`rule-${label.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
      <span>{label}</span>
    </div>
  );
}

export default function EngagementConsole() {
  const [tracks, setTracks] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [audit, setAudit] = useState([]);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [firing, setFiring] = useState(null);
  const [stats, setStats] = useState({});
  const [chainOk, setChainOk] = useState(null);
  const auditScrollRef = useRef(null);

  const fetchAll = async () => {
    try {
      const [tRes, wRes, aRes] = await Promise.all([
        axios.get('/hal/tracks'),
        axios.get('/hal/weapons'),
        axios.get('/audit/log?limit=30'),
      ]);
      setTracks(tRes.data.tracks || []);
      setStats(tRes.data.stats || {});
      setWeapons(wRes.data.weapons || []);
      setAudit(aRes.data.entries || []);
      // Auto-select first confirmed in-zone track if nothing chosen
      setSelectedTrackId((cur) => {
        if (cur && (tRes.data.tracks || []).some((t) => t.track_id === cur)) return cur;
        const cand = (tRes.data.tracks || []).find(
          (t) => t.confirmed && haversineM(ASSET.lat, ASSET.lng, t.lat, t.lng) <= ENGAGE_RADIUS_M,
        );
        return cand?.track_id || (tRes.data.tracks?.[0]?.track_id ?? null);
      });
    } catch (e) {
      // swallow — auto-poll will retry
    }
  };

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 1500);
    return () => clearInterval(id);
  }, []);

  // Auto-scroll audit tail to newest entry
  useEffect(() => {
    if (auditScrollRef.current) {
      auditScrollRef.current.scrollTop = 0;
    }
  }, [audit.length]);

  const selected = useMemo(
    () => tracks.find((t) => t.track_id === selectedTrackId) || null,
    [tracks, selectedTrackId],
  );

  const rangeM = selected ? haversineM(ASSET.lat, ASSET.lng, selected.lat, selected.lng) : 0;
  const inZone = rangeM <= ENGAGE_RADIUS_M;

  const fire = async (weaponId, weaponType) => {
    if (!selected) return;
    setFiring(`${weaponId}`);
    try {
      const body = {
        weapon_id: weaponId,
        track_id: selected.track_id,
        target_lat: selected.lat,
        target_lng: selected.lng,
        target_altitude: selected.altitude ?? 100,
      };
      const kind = WEAPON_KIND[weaponType]?.kind;
      if (kind === 'hard') body.burst_rounds = 24;
      else body.duration_s = 4;
      const { data } = await axios.post('/weapons/fire', body);
      if (data.permitted && data.fired) {
        toast.success(
          `${WEAPON_KIND[weaponType]?.label || weaponType} → engaged ${selected.track_id} (${data.ack?.state || 'ack'})`,
        );
      } else {
        toast.error(`ROE denied: ${data.reasons?.[0] || 'rule failure'}`);
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fire request failed');
    } finally {
      setFiring(null);
    }
  };

  const verifyChain = async () => {
    setChainOk(null);
    try {
      const { data } = await axios.get('/audit/verify');
      setChainOk(data.verified);
      toast[data.verified ? 'success' : 'error'](
        `Audit chain: ${data.verified ? 'verified' : 'TAMPERED'} (${data.entries_checked} entries)`,
      );
    } catch {
      setChainOk(false);
      toast.error('Audit verify failed');
    }
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950" data-testid="engagement-console">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
        <div>
          <h1 className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
            <Crosshair size={20} className="text-red-500" />
            Engagement Console
          </h1>
          <p className="text-[10px] uppercase font-mono tracking-widest text-zinc-500 mt-0.5">
            AOR Visakhapatnam · Eastern Naval Command · Single-Operator Authority
          </p>
        </div>
        <div className="flex items-center gap-3 text-[11px] font-mono uppercase text-zinc-400">
          <span>Tracks: <span className="text-sky-300">{stats.live_tracks ?? 0}</span></span>
          <span>Associations: <span className="text-emerald-300">{stats.associations ?? 0}</span></span>
          <button
            onClick={verifyChain}
            className="px-2 py-1 border border-zinc-700 hover:border-amber-500/60 hover:text-amber-300 rounded-sm flex items-center gap-1.5"
            data-testid="audit-verify-button"
          >
            <FileLock2 size={12} />
            Verify Chain
            {chainOk === true && <CheckCircle2 size={12} className="text-emerald-400" />}
            {chainOk === false && <AlertTriangle size={12} className="text-red-400" />}
          </button>
          <button
            onClick={fetchAll}
            className="px-2 py-1 border border-zinc-700 hover:border-sky-500/60 hover:text-sky-300 rounded-sm flex items-center gap-1.5"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </header>

      {/* Body: 3 columns */}
      <div className="flex-1 grid grid-cols-12 gap-3 p-3 overflow-hidden">
        {/* ── LEFT: Track list ───────────────────────────────────────── */}
        <section className="col-span-3 bg-zinc-900/40 border border-zinc-800 rounded-sm flex flex-col">
          <div className="px-3 py-2 border-b border-zinc-800 text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-2">
            <Activity size={12} className="text-sky-400" /> Live Tracks ({tracks.length})
          </div>
          <div className="flex-1 overflow-y-auto" data-testid="track-list">
            {tracks.length === 0 && (
              <div className="px-3 py-6 text-center text-[11px] font-mono text-zinc-600">
                No active tracks
              </div>
            )}
            {tracks.map((t) => {
              const Icon = DOMAIN_ICON[t.domain] || Plane;
              const range = haversineM(ASSET.lat, ASSET.lng, t.lat, t.lng);
              const inside = range <= ENGAGE_RADIUS_M;
              const isSelected = t.track_id === selectedTrackId;
              return (
                <button
                  key={t.track_id}
                  onClick={() => setSelectedTrackId(t.track_id)}
                  className={`w-full text-left px-3 py-2 border-b border-zinc-900 hover:bg-zinc-800/60 transition-colors ${
                    isSelected ? 'bg-zinc-800/80 border-l-2 border-l-sky-500' : ''
                  }`}
                  data-testid={`track-${t.track_id}`}
                >
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="flex items-center gap-1.5">
                      <Icon size={12} className={DOMAIN_COLOR[t.domain] || 'text-zinc-400'} />
                      <span className="text-zinc-200">{t.track_id}</span>
                    </span>
                    <span className={inside ? 'text-emerald-400' : 'text-zinc-500'}>
                      {(range / 1000).toFixed(1)}km
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    <span>{t.confirmed ? '✅ confirmed' : `⌛ ${t.hits} hits`}</span>
                    <span>{t.speed_mps ? `${(t.speed_mps * 3.6).toFixed(0)} km/h` : '—'}</span>
                  </div>
                  {t.sensors?.length > 0 && (
                    <div className="mt-1 text-[9px] font-mono text-sky-500/70 truncate">
                      {t.sensors.join(', ')}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        {/* ── CENTER: Selected track + ROE + Fire ───────────────────── */}
        <section className="col-span-6 flex flex-col gap-3 overflow-y-auto" data-testid="engagement-panel">
          {!selected && (
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-6 text-center font-mono text-sm text-zinc-500">
              Select a track from the list
            </div>
          )}

          {selected && (
            <>
              {/* Target card */}
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">
                      TARGET
                    </div>
                    <div className="text-lg font-bold tracking-tight text-zinc-100 flex items-center gap-2">
                      {selected.track_id}
                      {selected.confirmed ? (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 rounded-sm">
                          CONFIRMED
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/40 text-amber-300 rounded-sm">
                          PROVISIONAL
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-mono uppercase text-zinc-500">RANGE</div>
                    <div className={`text-2xl font-mono ${inZone ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(rangeM / 1000).toFixed(2)} km
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 text-[11px] font-mono">
                  <div>
                    <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Domain</div>
                    <div className={`${DOMAIN_COLOR[selected.domain]} uppercase`}>{selected.domain}</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Speed</div>
                    <div className="text-zinc-200">{(selected.speed_mps * 3.6).toFixed(0)} km/h</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Altitude</div>
                    <div className="text-zinc-200">{(selected.altitude ?? 0).toFixed(1)} m</div>
                  </div>
                  <div>
                    <div className="text-zinc-500 uppercase tracking-widest text-[9px]">Hits</div>
                    <div className="text-zinc-200">{selected.hits}</div>
                  </div>
                </div>
                <div className="mt-3 text-[10px] font-mono text-zinc-500">
                  Contributing sensors: <span className="text-sky-400">{(selected.sensors || []).join(', ') || '—'}</span>
                </div>
              </div>

              {/* ROE light bar */}
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-3" data-testid="roe-lights">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                    {inZone ? <ShieldCheck size={12} className="text-emerald-400" /> : <ShieldOff size={12} className="text-red-400" />}
                    Rules of Engagement
                  </div>
                  <div className="text-[10px] font-mono text-zinc-500">
                    Live evaluation
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <RuleLight ok={inZone} label="In Engagement Zone (5km)" />
                  <RuleLight ok={true} label="Outside No-Fire Zone" />
                  <RuleLight ok={true} label="Not Friendly IFF" />
                  <RuleLight ok={selected.confirmed} label="Confirmed Track" />
                </div>
              </div>

              {/* Weapons grid */}
              <div className="bg-zinc-900/40 border border-zinc-800 rounded-sm p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
                  <Flame size={12} className="text-red-400" />
                  Weapon Loadout — single-click engage
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {weapons.map((w) => {
                    const meta = WEAPON_KIND[w.weapon_type] || { kind: 'soft', label: w.weapon_type, accent: 'zinc' };
                    const isHard = meta.kind === 'hard';
                    const accent = meta.accent;
                    const isFiring = firing === w.weapon_id;
                    return (
                      <button
                        key={w.weapon_id}
                        onClick={() => fire(w.weapon_id, w.weapon_type)}
                        disabled={!inZone || isFiring}
                        data-testid={`fire-${w.weapon_id}`}
                        className={`group text-left p-3 border rounded-sm transition-all font-mono ${
                          isFiring
                            ? `border-${accent}-400 bg-${accent}-500/20 animate-pulse`
                            : `border-zinc-800 hover:border-${accent}-500/70 hover:bg-${accent}-500/10`
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-xs text-zinc-200 uppercase tracking-tight">{meta.label}</div>
                          <span
                            className={`text-[9px] uppercase tracking-widest px-1 py-0.5 rounded-sm border ${
                              isHard
                                ? 'border-red-500/40 text-red-300 bg-red-500/10'
                                : 'border-emerald-500/40 text-emerald-300 bg-emerald-500/10'
                            }`}
                          >
                            {isHard ? 'HARD' : 'SOFT'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-zinc-500">
                          <span>{w.weapon_id}</span>
                          <span>
                            {w.ammo_remaining != null
                              ? `${w.ammo_remaining}${isHard ? ' rds' : ''}`
                              : 'Continuous'}
                          </span>
                        </div>
                        <div
                          className={`mt-2 text-[10px] font-bold uppercase tracking-widest ${
                            isFiring ? `text-${accent}-300` : `text-${accent}-400 group-hover:text-${accent}-200`
                          }`}
                        >
                          {isFiring ? '◉ Engaging…' : '▸ Engage'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {!inZone && (
                  <div className="mt-3 text-[11px] font-mono text-amber-400 flex items-center gap-1.5">
                    <AlertTriangle size={12} />
                    Target outside 5 km engagement zone — engage disabled.
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* ── RIGHT: Audit tail ─────────────────────────────────────── */}
        <section className="col-span-3 bg-zinc-900/40 border border-zinc-800 rounded-sm flex flex-col">
          <div className="px-3 py-2 border-b border-zinc-800 text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileLock2 size={12} className="text-amber-400" /> Audit Tail
            </span>
            <span className="text-[10px] text-zinc-500">live · {audit.length}</span>
          </div>
          <div
            ref={auditScrollRef}
            className="flex-1 overflow-y-auto text-[10px] font-mono"
            data-testid="audit-tail"
          >
            {audit.length === 0 && (
              <div className="px-3 py-4 text-center text-zinc-600">No entries yet</div>
            )}
            {audit.map((e) => {
              const colour =
                e.event_type === 'weapon_fire'
                  ? 'text-red-400 border-l-red-500/50'
                  : e.event_type === 'roe_gate'
                  ? 'text-amber-400 border-l-amber-500/50'
                  : e.event_type === 'authorization'
                  ? 'text-sky-400 border-l-sky-500/50'
                  : 'text-zinc-400 border-l-zinc-600';
              const summary =
                e.event_type === 'weapon_fire'
                  ? `${e.payload?.ack?.state} → ${e.payload?.ack?.weapon_id} on ${e.payload?.command?.target_track_id}`
                  : e.event_type === 'roe_gate'
                  ? `${e.payload?.gate?.permitted ? '✓ permitted' : '✗ denied'} · ${e.payload?.weapon_id} → ${e.payload?.track_id}`
                  : e.event_type === 'authorization'
                  ? `${e.payload?.weapon_id} → ${e.payload?.track_id}`
                  : '';
              return (
                <div key={e.id} className={`px-3 py-1.5 border-l-2 ${colour} border-b border-zinc-900/70`}>
                  <div className="flex items-center justify-between">
                    <span className="uppercase tracking-wider">{e.event_type}</span>
                    <span className="text-zinc-600">#{e.seq}</span>
                  </div>
                  <div className="text-zinc-300 truncate">{summary}</div>
                  <div className="flex items-center justify-between mt-0.5 text-zinc-600">
                    <span>{e.actor || '—'}</span>
                    <span>{timeAgo(e.ts)} ago</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
